"use client";
import styled from "styled-components";
import media from "@/styles/mediaQueries";
import React from "react";
import IconArrowRight from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconArrowRight.js";
import Link from "next/link";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { channel } from "@/lib/Types/Channel";
import { DestinationIDData } from "@/lib/Types/DestinationIDData";
import {
  PATH_REISEZIELE_URLAUB,
  PATH_REISEZIELE_HOTELS,
  PATH_LISTE_URLAUB,
  PATH_LISTE_HOTELS,
} from "@/lib/StaticData/paths";
import { Text } from "@lmn/design-system";
import { useHotelInfoStore } from "@/lib/stores/hotelInfoStore";
import { buildBackToHotelUrl } from "@/app/buchung/helpers/buildBackToHotelUrl";

export const Container = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  svg {
    min-width: 12px;
  }
`;

export const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  &:hover {
    text-decoration: underline;
  }
`;

export const BreadcrumbItem = styled.span`
  &:last-child p {
    font-weight: bold;
  }
`;

export const StyledIconArrowRight = styled(IconArrowRight)`
  fill: ${({ theme }) =>
    theme.kallisto.color.text.interactive.textPrimaryDefault};
`;

export const DesktopOnlyLabel = styled.span`
  display: none;
  ${media.screenMdMin`
    display: inline;
  `}
`;

interface BreadcrumbsClientProps {
  data: DestinationIDData[];
  channelType: channel.HOTEL | channel.PACKAGE;
  isHotelPage?: boolean;
  isCheckoutPage?: boolean;
  locationOverrides?: { country?: string; region?: string; city?: string };
}

export default function BreadcrumbsClient({
  data,
  channelType,
  isHotelPage = false,
  isCheckoutPage = false,
  locationOverrides,
}: BreadcrumbsClientProps) {
  const searchParams = useReactiveSearchParams();
  const hotelName = useHotelInfoStore((state) => state.hotelInfo.hotelName);
  const hotelCid = useHotelInfoStore((state) => state.hotelInfo.hotelCid);

  // When the hotel page URL has no country/region/city params, the server
  // derives them from the hotel info API. Inject them into breadcrumb link
  // URLs so clicking e.g. "Reiseziel: Türkei" navigates with the correct
  // country filter instead of showing "beliebig" results.
  const applyLocationOverrides = (params: URLSearchParams) => {
    if (!locationOverrides) return;
    if (locationOverrides.country && !params.has("country")) {
      params.set("country", locationOverrides.country);
    }
    if (locationOverrides.region && !params.has("region")) {
      params.set("region", locationOverrides.region);
    }
    if (locationOverrides.city && !params.has("city")) {
      params.set("city", locationOverrides.city);
    }
  };

  // create a link to the reiseziele page
  const createReisezieleLinkUrl = () => {
    const newParams = new URLSearchParams(searchParams.toString());
    applyLocationOverrides(newParams);
    newParams.delete("city");
    newParams.delete("region");
    newParams.delete("page");
    const basePath =
      channelType === channel.PACKAGE
        ? PATH_REISEZIELE_URLAUB
        : PATH_REISEZIELE_HOTELS;
    return `${basePath}?${newParams.toString()}`;
  };

  // create a link to the liste page
  const createListeLinkUrl = () => {
    const newParams = new URLSearchParams(searchParams.toString());
    applyLocationOverrides(newParams);
    newParams.delete("page");

    const basePath =
      channelType === channel.PACKAGE ? PATH_LISTE_URLAUB : PATH_LISTE_HOTELS;
    return `${basePath}?${newParams.toString()}`;
  };

  const createHotelDetailUrl = () =>
    buildBackToHotelUrl({
      searchParams,
      hotelCid: hotelCid ?? undefined,
      channel: channelType,
    });

  if (data.length === 0) {
    return null;
  }


  const steps: { label: string; name: string; href: string | null }[] = [
    {
      label: "Reiseziel: ",
      name: data.some((d) => d.level === "COUNTRY")
        ? data[0].name
        : "beliebte Reiseziele ",
      href: createReisezieleLinkUrl(),
    },
    {
      label: "Hotels für: ",
      name: data[data.length - 1].name,
      href: isHotelPage || isCheckoutPage ? createListeLinkUrl() : null,
    },
  ];

  if ((isHotelPage || isCheckoutPage) && hotelName) {
    steps.push({
      label: "Zimmer & Preise für: ",
      name: hotelName,
      href: isCheckoutPage ? createHotelDetailUrl() : null,
    });
  }

  if (isCheckoutPage) {
    steps.push({
      label: "Buchung: ",
      name: "Koffer packen",
      href: null,
    });
  }

  return (
    <Container data-testid="breadcrumbs">
      {steps.map((step, idx) => {
        const content = (
          <Text variant="BodySmall">
            <DesktopOnlyLabel>{step.label}</DesktopOnlyLabel>
            {step.name}
          </Text>
        );
        return (
          <React.Fragment key={step.label}>
            {idx > 0 && <StyledIconArrowRight size={12} />}
            <BreadcrumbItem>
              {step.href ? (
                <StyledLink href={step.href} prefetch={false}>
                  {content}
                </StyledLink>
              ) : (
                content
              )}
            </BreadcrumbItem>
          </React.Fragment>
        );
      })}
    </Container>
  );
}
